import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Play } from "lucide-react";
import { reApi, errMsg } from "@/lib/residentApi";
import { btnGold } from "@/components/nds/windows/finance/parts";
import WorkflowContent from "@/components/nds/windows/finance/WorkflowContent";
import { CHECKIN_CFG } from "@/components/nds/windows/ReCheckinsContent";

const etToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

/** Dashboard "Fix here" for the Weekly Check-In card: opens this resident's draft straight away, or shows what's open and starts one. */
export default function WeeklyCheckinFix({ residentId, residentName, onChanged }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    reApi.get("/checkins/areas-preview", { resident_id: residentId }).then((x) => { setD(x); setOpen(x.draft_id); }).catch((e) => toast.error(errMsg(e)));
  }, [residentId]);
  const start = async () => {
    setBusy(true);
    try { const c = await reApi.post("/checkins", { resident_id: residentId, checkin_date: etToday() }); toast.success("Check-in started"); setOpen(c.id); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return <Loader2 className="h-4 w-4 animate-spin" />;
  if (open) return <div data-testid="rd-fix-weekly-checkin" className="-mx-5"><WorkflowContent key={open} cfg={CHECKIN_CFG} initial={{ open, only: residentId, resident: { resident_id: residentId, name: residentName } }} /></div>;
  const todo = d.areas.filter((a) => !a.ok && a.key !== "questions");
  const l = d.latest;
  return (
    <div data-testid="rd-fix-weekly-checkin" className="space-y-3">
      <div className="text-sm text-nex-silver" data-testid="wk-fix-latest">
        {!l ? "No weekly check-in on file yet." : `Last check-in ${l.checkin_date} · ${l.status === "draft" ? "draft" : l.counted ? "counted" : `not counted (${l.not_counted_reason || "too soon"})`}`}
      </div>
      <button type="button" disabled={busy} onClick={start} data-testid="wk-fix-start" className={`${btnGold} inline-flex items-center gap-1.5`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Start this week's check-in</button>
      <p className="text-[11px] text-nex-muted">Starting opens the check-in questions and the to-do list below, where you fix each item or note why not, then submit.</p>
      <div className="border border-white/10 rounded-sm p-3 space-y-1.5" data-testid="wk-fix-preview">
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-nex-gold">Still to do for {residentName} · {todo.length}</div>
        {todo.map((a) => <div key={a.key} className="flex gap-2 text-xs" data-testid={`wk-fix-preview-${a.key}`}><AlertTriangle className="h-3.5 w-3.5 text-yellow-300 shrink-0" /><span className="text-nex-silver">{a.label}</span><span className="text-nex-muted break-words min-w-0">{a.text}</span></div>)}
        <div className="flex gap-2 text-xs text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" />{d.areas.filter((a) => a.ok).length} areas complete</div>
      </div>
    </div>
  );
}
